import 'rsuite/dist/rsuite-no-reset.min.css';

import { Button } from '@tremor/react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'rsuite/DatePicker';
import { addWebChatMessage, getWebChatMessages } from './actions';

type Message = Omit<WebChatMessage, 'conversation_id' | 'created_at'> & {
  pending?: boolean;
};

const TextMessage = ({
  children,
  outbound,
  widgetTheme,
}: {
  children: React.ReactNode;
  outbound: boolean;
  widgetTheme?: string;
}) => {
  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s<]+)|(bit\.ly\/[^\s<]+)/g;
    const matches = Array.from(text.matchAll(urlRegex));
    let lastIndex = 0;
    const elements: JSX.Element[] = [];

    matches.forEach((match, i) => {
      if (match.index && match.index > lastIndex) {
        elements.push(
          <span key={`text-${i}`}>{text.slice(lastIndex, match.index)}</span>
        );
      }
      const url = match[0].startsWith('http')
        ? match[0]
        : `https://${match[0]}`;

      elements.push(
        <a
          key={`link-${i}`}
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className={`underline cursor-pointer ${outbound ? 'text-blue-600 hover:text-blue-800' : 'text-white hover:text-gray-100'}`}
          style={{ pointerEvents: 'auto' }}
          onClick={(e) => {
            e.preventDefault();
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
        >
          {match[0]}
        </a>
      );

      lastIndex = (match.index || 0) + match[0].length;
    });

    if (lastIndex < text.length) {
      elements.push(<span key='text-end'>{text.slice(lastIndex)}</span>);
    }

    return elements;
  };

  return (
    <p
      className={`${
        outbound
          ? 'bg-gray-100 rounded-md text-left'
          : `${widgetTheme ? '' : 'bg-brandBlue'} rounded-md text-white`
      } p-4 rounded-md text-left relative`}
      style={{
        ...(!outbound && widgetTheme ? { backgroundColor: widgetTheme } : {}),
        pointerEvents: 'auto',
      }}
    >
      {typeof children === 'string' ? renderTextWithLinks(children) : children}
    </p>
  );
};

const formatDateTime = (dateTime: DateTime) => {
  return dateTime.toFormat('LLL dd, h:mm a');
};

const getValue = (value: string, valueType: 'STRING' | 'DATETIME') => {
  switch (valueType) {
    case 'STRING':
      return value;
    case 'DATETIME':
      return formatDateTime(DateTime.fromISO(value));
    default:
      return value;
  }
};

const ChatMessageDataSelect = ({
  latest,
  onClick,
}: {
  latest: boolean;
  onClick?: (
    event: React.SyntheticEvent,
    type: 'MESSAGE' | 'BUTTON_SELECT',
    dataType: 'STRING' | 'DATETIME'
  ) => void;
}) => {
  const [value, setValue] = useState<Date | null>(new Date());
  return (
    <div className='flex items-center min-w-[200px] bg-gray-100 mb-2 p-3 rounded-md mr-2 hover:bg-gray-200'>
      <DatePicker
        placement='topStart'
        format='MMM dd, h:mm aa'
        value={value}
        className='mr-2'
        onChange={(value) => {
          if (value) {
            const d = DateTime.fromJSDate(value);
            const hour = Math.ceil((d.hour * 60 + d.minute) / 60);
            const newD = d.set({
              hour: hour,
              minute: 0,
              second: 0,
              millisecond: 0,
            });
            setValue(newD.toJSDate());
            return;
          }

          setValue(value);
        }}
        disabled={!latest}
        showMeridian
      />
      <Button
        key='other'
        style={{
          height: '32px',
        }}
        variant='secondary'
        data-value={value?.toISOString()}
        onClick={(e) => onClick && onClick(e, 'BUTTON_SELECT', 'DATETIME')}
        disabled={!latest}
      >
        Confirm
      </Button>
    </div>
  );
};

const ChatMessage = ({
  message,
  addMessage,
  latest,
  widgetTheme,
}: {
  message: Message;
  latest: boolean;
  widgetTheme?: string;
  addMessage?: (message: string, data?: any) => void;
}) => {
  const content = useMemo(() => {
    switch (message.kind) {
      case 'BUTTON_SELECT':
        const options = message.data as {
          options: string[];
          type: 'STRING' | 'DATETIME';
        };
        return (
          <div className='flex flex-col'>
            <div className='flex'>
              <TextMessage
                outbound={message.outbound}
                widgetTheme={widgetTheme}
              >
                {message.message}
              </TextMessage>
            </div>
            <div className='flex flex-col w-full'>
              <div className='py-2 flex flex-row items-center flex-wrap max-w-[400px]'>
                {options.options.map((option) => (
                  <button
                    key={option}
                    data-value={option}
                    className='text-left min-w-[50px] mb-2 bg-gray-100 p-4 rounded-md mr-2 hover:bg-gray-200'
                    onClick={(e) => {
                      if (!latest || !addMessage) return;
                      const value = e.currentTarget.getAttribute('data-value');
                      if (!value) return;

                      switch (options.type) {
                        case 'DATETIME': {
                          const date = DateTime.fromISO(value || '');
                          return addMessage(formatDateTime(date), {
                            type: 'DATETIME',
                            value: date.valueOf(),
                          });
                        }
                        case 'STRING': {
                          return addMessage(value, {
                            type: 'STRING',
                          });
                        }
                        default:
                          return;
                      }
                    }}
                    disabled={!latest}
                  >
                    {getValue(option, options.type)}
                  </button>
                ))}
                {options.type === 'DATETIME' && (
                  <ChatMessageDataSelect
                    latest={latest}
                    onClick={(e) => {
                      if (!latest || !addMessage) return;
                      const value = e.currentTarget.getAttribute('data-value');
                      const date = DateTime.fromISO(value || '');
                      console.log('parsing', value, date);
                      if (!date.isValid) return;

                      console.log(
                        'adding',
                        formatDateTime(date),
                        date.valueOf()
                      );

                      return addMessage(formatDateTime(date), {
                        type: 'DATETIME',
                        value: date.valueOf(),
                      });
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      case 'MESSAGE':
      default: {
        return (
          <TextMessage outbound={message.outbound} widgetTheme={widgetTheme}>
            {message.message}
          </TextMessage>
        );
      }
    }
  }, [latest, addMessage, message, widgetTheme]);

  return (
    <li
      key={message.id}
      className={`flex ${
        message.outbound === false ? 'ml-10 justify-end' : 'mr-10'
      }`}
    >
      {content}
    </li>
  );
};

export default function ChatWidgetMessages({
  conversationId,
  completed,
  options = {
    scroll: true,
    input: true,
    hfull: false,
  },
  demo = false,
  widgetTheme,
}: {
  conversationId: string;
  completed: boolean;
  options?: {
    scroll: boolean;
    input: boolean;
    hfull: boolean;
  };
  demo?: boolean;
  widgetTheme?: string;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [updateReceiveTrigger, setUpdateReceiveTrigger] = useState(false);
  const [closed, setClosed] = useState(false);
  const [expectedNumMessages, setExpectedNumMessages] = useState(
    messages.length || 1
  );
  const [newMessage, setNewMessage] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const waitingRef = useRef(false);
  const disableScroll = useRef(false);

  const pendingMessage = messages[messages.length - 1]?.pending || false;

  // Scroll to bottom whenever messages update
  useEffect(() => {
    const smoothScrollToBottom = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const maxScrollTop = container.scrollHeight - container.clientHeight;
      let currentScrollTop = container.scrollTop;
      const scrollBy = 10; // Adjust this value for smoother or faster scrolling

      const step = () => {
        if (currentScrollTop < maxScrollTop) {
          currentScrollTop += scrollBy;
          container.scrollTop = currentScrollTop;
          window.requestAnimationFrame(step);
        } else {
          container.scrollTop = maxScrollTop;
        }
      };

      window.requestAnimationFrame(step);
    };
    const fastScrollToBottom = () => {
      const container = messagesContainerRef.current;
      if (!container) return;
      container.scrollTop = container.scrollHeight - container.clientHeight;
    };

    // if (disableScroll.current) return

    if (waitingRef) {
      fastScrollToBottom();
    } else if (options.scroll) {
      smoothScrollToBottom();
    } else {
      fastScrollToBottom();
    }
  }, [messages, options.scroll]);

  // Send message to user when they send a message
  const receiveMessageHandler = async (conversationId: string) => {
    try {
      const res = await fetch(
        `/api/web-chat/receive-message?conversationId=${encodeURIComponent(
          conversationId
        )}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        setUpdateReceiveTrigger(!updateReceiveTrigger); // Trigger a re-fetch of conversations
        throw new Error('API response was not ok.');
      }

      const json = (await res.json()) as unknown as { closed: boolean };
      if (json.closed) {
        setClosed(true);
      }

      setUpdateReceiveTrigger(!updateReceiveTrigger); // Trigger a re-fetch of conversations
      waitingRef.current = false;
    } catch (err) {
      console.error('Error starting conversation:', err);
    }
  };

  const addMessage = (newMessage: string, data?: any) => {
    // Add message to Supabase web_chat_messages table
    const _addMessage = async () => {
      const { data: messages, error } = await addWebChatMessage(
        conversationId,
        newMessage,
        data
      );

      if (error || !messages) {
        console.error('Error adding message:', error);
        return;
      }
      setNewMessage('');
    };

    _addMessage();

    // Optimistically add message to messages array
    setMessages([
      ...messages,
      {
        id: 'new',
        outbound: false,
        message: newMessage,
        pending: true,
        data: '',
        kind: 'MESSAGE',
      },
    ]);

    // Expect an outbound message from the agent
    setExpectedNumMessages(messages.length + 2);

    // Receive and send outbound message to user in response to inbound message
    waitingRef.current = true;
    receiveMessageHandler(conversationId);
  };

  const handleSubmitMessage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!newMessage) {
      return;
    }

    addMessage(newMessage);
  };

  // Get all conversations for the team from Supabase

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    const fetchMessages = async () => {
      const { data: messages, error } =
        await getWebChatMessages(conversationId);

      if (error || !messages) {
        console.error('Error fetching messages:', error);
        return;
      }
      setMessages(messages);
    };

    fetchMessages();
  }, [conversationId, updateReceiveTrigger]);

  // Periodically refetch messages
  useEffect(() => {
    const timer = setInterval(() => {
      if (!waitingRef.current) {
        disableScroll.current = true;
        setUpdateReceiveTrigger((last) => !last);
      }
    }, 5000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className='divide-y divide-gray-300/50 border-t border-gray-300/50'>
      <div
        ref={messagesContainerRef}
        className={`space-y-6 py-8 text-base leading-7 text-gray-600 overflow-y-auto`}
        style={{ height: options.hfull ? '100%' : demo ? '352px' : '400px' }}
      >
        <ul className='space-y-4 px-4'>
          {messages.map((item, index) => (
            <ChatMessage
              key={`${item.message}-${index}`}
              message={item}
              latest={index === messages.length - 1}
              addMessage={addMessage}
              widgetTheme={widgetTheme}
            />
          ))}
        </ul>
        {!completed &&
          pendingMessage &&
          expectedNumMessages > messages.length && (
            <ul>
              <div className='px-4 py-1 flex'>
                <div className='flex w-12 place-content-center align-middle items-center mr-2 bg-gray-100 p-4 px-8 rounded-md'>
                  <div className='snippet' data-title='dot-flashing'>
                    <div className='stage'>
                      <div className='dot-flashing'></div>
                    </div>
                  </div>
                </div>
              </div>
            </ul>
          )}
        {closed && (
          <div className='flex justify-center'>
            <Button
              variant='secondary'
              className='border-gray-500 text-gray-500'
              disabled
            >
              Chat Closed
            </Button>
          </div>
        )}
      </div>
      {options.input && (
        <div className='flex flex-col flex-50'>
          <form
            onSubmit={handleSubmitMessage}
            className='p-4 pb-0 flex gap-2 text-base font-semibold leading-7'
          >
            <input
              name='message'
              value={!closed ? newMessage : undefined}
              autoComplete='off'
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={!closed ? 'Chat with us' : 'Chat closed'}
              className='px-2 py-1.5 border rounded-md flex-1 font-normal focus:outline-none focus:border-blue-400'
              disabled={closed}
            />
            <button
              className={`px-2.5 rounded-md text-white ${
                widgetTheme ? '' : 'bg-brandBlue'
              } hover:brightness-90 transition-all`}
              style={widgetTheme ? { backgroundColor: widgetTheme } : undefined}
            >
              {/* prettier-ignore */}
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" x2="11" y1="2" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
